import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { t } from '@/i18n';
import { toastApiError } from '@/queries/common';
import { addDamage } from '@/queries/damages/add-damages';
import { updateDamage } from '@/queries/damages/update-damages';
import { moveDamage } from '@/queries/damages/move-damage';
import { updateDamagePolygon } from '@/queries/damage-polygons/update-damage-polygon';
import type { DamageType } from '@/common/damage';

type UseDamageMutationsProps = {
	assetId: string;
	inspectionId: string;
	damageId?: string;
	onCreateSuccess?: (data: DamageType) => void;
	onUpdateSuccess?: (data: DamageType) => void;
	onMoveSuccess?: (targetInspectionId: string) => void;
};

type MoveMutationPayload = {
	targetInspectionId: string;
	polygons?: { id: string }[]; // Array of polygons to move
};

export function useDamageMutations({
	assetId,
	inspectionId,
	damageId,
	onCreateSuccess,
	onUpdateSuccess,
	onMoveSuccess,
}: UseDamageMutationsProps) {
	const queryClient = useQueryClient();

	// --- Shared Invalidation Logic ---
	const invalidateAll = async () => {
		if (damageId) {
			await queryClient.invalidateQueries({
				queryKey: ['damage', assetId, inspectionId, damageId],
			});
		}
		await queryClient.invalidateQueries({ queryKey: ['damages'] });

		// Refresh the map polygons (in case metadata changed affecting display)
		await queryClient.invalidateQueries({ queryKey: ['damage-polygons', assetId] });
	};

	// --- CREATE ---
	const createMutation = useMutation({
		mutationFn: (data: Omit<DamageType, 'id'>) => addDamage(assetId, inspectionId, data),
		onSuccess: async (newItem) => {
			toast.success(t('damages.toast.createSuccess'));
			await invalidateAll();
			onCreateSuccess?.(newItem);
		},
		onError: (error) => toastApiError(error),
	});

	// --- UPDATE ---
	const updateMutation = useMutation({
		mutationFn: (data: Omit<DamageType, 'id'>) => updateDamage(damageId!, data),
		onSuccess: async (updatedItem) => {
			toast.success(t('damages.toast.updateSuccess'));
			await invalidateAll();
			onUpdateSuccess?.(updatedItem);
		},
		onError: (error) => toastApiError(error),
	});

	// --- MOVE ---
	const moveMutation = useMutation({
		mutationFn: async ({ targetInspectionId, polygons }: MoveMutationPayload) => {
			// Move the Damage entity
			const result = await moveDamage(damageId!, { inspectionId: targetInspectionId, assetId });

			// If there are polygons, move them to the new inspection as well
			if (polygons && polygons.length > 0) {
				try {
					await Promise.all(
						polygons.map((polygon) =>
							updateDamagePolygon(polygon.id, { inspectionId: targetInspectionId })
						)
					);
				} catch (error) {
					console.error('Failed to move associated polygons', error);
					toast.error(t('damages.toast.movePolygonsFailed'));
				}
			}

			return result;
		},
		onSuccess: async (_, variables) => {
			if (variables.polygons && variables.polygons.length > 0) {
				toast.success(t('damages.toast.moveWithPolygonsSuccess'));
			} else {
				toast.success(t('damages.toast.moveSuccess'));
			}
			await invalidateAll();
			onMoveSuccess?.(variables.targetInspectionId);
		},
		onError: (error) => toastApiError(error),
	});

	return {
		createMutation,
		updateMutation,
		moveMutation,
		isSubmitting:
			createMutation.isPending ||
			updateMutation.isPending ||
			moveMutation.isPending,
	};
}
